import {
  ACTIVE_DESIGN_SYSTEM_VERSION,
  getDesignSystem,
} from '../../layouts/theme/designSystem';
import type {DesignSystemVersion} from '../../layouts/theme/designSystem';

export type HeaderNavMenuTriggerPalette = {
  color: string;
  backgroundColor: string;
  backgroundColorAlt: string;
  hoverBackgroundColor: string;
  openBackgroundColor: string;
};

export type HeaderNavMenuTriggerTokens = {
  closedBorderRadius: string;
  openBorderRadius: string;
  openBoxShadow: string;
  closedBoxShadow: string;
  closedHeight?: string;
  openHeight?: string;
  nestChromeInButton?: boolean;
  light: HeaderNavMenuTriggerPalette;
  dark: HeaderNavMenuTriggerPalette;
};

export type HeaderNavMenuPaperPalette = {
  backgroundColor: string;
  backgroundColorAlt: string;
};

export type HeaderNavMenuPaperTokens = {
  border?: string;
  borderRadius: string;
  padding: string;
  boxShadow: string;
  minWidth?: number;
  width?: {xs: string; sm: string};
  marginTop?: number | string;
  menuListPaddingY?: number;
  menuListPaddingX?: number;
  light: HeaderNavMenuPaperPalette;
  dark: HeaderNavMenuPaperPalette;
};

export type HeaderNavMenuItemTokens = {
  backgroundColor: string;
  hoverBackgroundColor: string;
  color: string;
  borderRadius: string;
  minWidth?: string;
  minHeight?: number;
  marginX?: number;
  marginY?: number;
  paddingX?: number;
  paddingY?: number;
  fontWeight?: number | string;
  iconMinWidth: number;
};

export type HeaderNavWrapperTokens = {
  backgroundColor: string;
  backgroundImage: string;
  border?: string;
  borderColor?: string;
  boxShadow?: string;
};

export type HeaderNavDefinition = {
  wrapper: HeaderNavWrapperTokens;
  headerMenuTrigger: HeaderNavMenuTriggerTokens;
  contextMenuTrigger: HeaderNavMenuTriggerTokens;
  headerModuleMenuPaper: HeaderNavMenuPaperTokens;
  contextMenuPaper: HeaderNavMenuPaperTokens;
  avatarMenuPaper: HeaderNavMenuPaperTokens;
  menuItem: HeaderNavMenuItemTokens;
};

const buildHeaderNav20260615 = (version: DesignSystemVersion): HeaderNavDefinition => {
  const {greenColors, orangeColors} = getDesignSystem(version);

  return {
    wrapper: {
      backgroundColor: '#fff',
      backgroundImage: 'none',
      boxShadow: 'none',
    },
    headerMenuTrigger: {
      closedBorderRadius: '10px',
      openBorderRadius: '10px 10px 0px 0px',
      openBoxShadow: 'none',
      closedBoxShadow: 'none',
      closedHeight: '100%',
      openHeight: '120%',
      nestChromeInButton: false,
      light: {
        color: 'text.secondary',
        backgroundColor: '#fff',
        backgroundColorAlt: '#fff',
        hoverBackgroundColor: orangeColors[7],
        openBackgroundColor: orangeColors[7],
      },
      dark: {
        color: 'white',
        backgroundColor: greenColors[600],
        backgroundColorAlt: greenColors[600],
        hoverBackgroundColor: greenColors[500],
        openBackgroundColor: 'background.menu',
      },
    },
    contextMenuTrigger: {
      closedBorderRadius: '10px',
      openBorderRadius: '10px 10px 0px 0px',
      openBoxShadow: '0px 4px 4px rgba(0, 0, 0, 0.06)',
      closedBoxShadow: 'none',
      light: {
        color: 'black',
        backgroundColor: orangeColors[4],
        backgroundColorAlt: orangeColors[5],
        hoverBackgroundColor: greenColors[40],
        openBackgroundColor: orangeColors[4],
      },
      dark: {
        color: 'white',
        backgroundColor: greenColors[600],
        backgroundColorAlt: greenColors[600],
        hoverBackgroundColor: greenColors[500],
        openBackgroundColor: 'background.menu',
      },
    },
    headerModuleMenuPaper: {
      borderRadius: '0px 10px 10px 10px',
      padding: '16px 32px 16px 0px',
      boxShadow: 'none',
      light: {
        backgroundColor: orangeColors[7],
        backgroundColorAlt: orangeColors[7],
      },
      dark: {
        backgroundColor: 'background.menu',
        backgroundColorAlt: 'background.menu',
      },
    },
    contextMenuPaper: {
      borderRadius: '10px 0px 10px 10px',
      padding: '16px 48px 16px 0px',
      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.06)',
      width: {xs: '250px', sm: '300px'},
      light: {
        backgroundColor: orangeColors[4],
        backgroundColorAlt: orangeColors[5],
      },
      dark: {
        backgroundColor: 'background.menu',
        backgroundColorAlt: 'background.menu',
      },
    },
    avatarMenuPaper: {
      borderRadius: '10px',
      padding: '16px 32px 16px 0px',
      boxShadow: 'none',
      light: {
        backgroundColor: orangeColors[7],
        backgroundColorAlt: orangeColors[7],
      },
      dark: {
        backgroundColor: 'background.menu',
        backgroundColorAlt: 'background.menu',
      },
    },
    menuItem: {
      backgroundColor: orangeColors[7],
      hoverBackgroundColor: orangeColors[3],
      color: greenColors[700],
      borderRadius: '10px',
      minWidth: '200px',
      marginX: 2,
      fontWeight: 'bold',
      iconMinWidth: 32,
    },
  };
};

const buildHeaderNav20260616 = (): HeaderNavDefinition => {
  const {greenColors, orangeColors, neutral, empionOrangeGradient} = getDesignSystem('20260616');

  return {
    wrapper: {
      backgroundColor: 'transparent',
      backgroundImage: empionOrangeGradient.navbar,
      border: '1px solid',
      borderColor: neutral[200],
      boxShadow: '0 1px 2px rgba(30, 42, 49, 0.04)',
    },
    headerMenuTrigger: {
      closedBorderRadius: '8px',
      openBorderRadius: '8px',
      openBoxShadow: 'none',
      closedBoxShadow: 'none',
      light: {
        color: 'text.primary',
        backgroundColor: 'transparent',
        backgroundColorAlt: 'transparent',
        hoverBackgroundColor: neutral[200],
        openBackgroundColor: 'transparent',
      },
      dark: {
        color: 'white',
        backgroundColor: 'transparent',
        backgroundColorAlt: 'transparent',
        hoverBackgroundColor: greenColors[500],
        openBackgroundColor: 'transparent',
      },
    },
    contextMenuTrigger: {
      closedBorderRadius: '10px',
      openBorderRadius: '10px 10px 0px 0px',
      openBoxShadow: '0px 4px 4px rgba(0, 0, 0, 0.06)',
      closedBoxShadow: 'none',
      light: {
        color: 'black',
        backgroundColor: orangeColors[4],
        backgroundColorAlt: orangeColors[5],
        hoverBackgroundColor: greenColors[40],
        openBackgroundColor: orangeColors[4],
      },
      dark: {
        color: 'white',
        backgroundColor: greenColors[600],
        backgroundColorAlt: greenColors[600],
        hoverBackgroundColor: greenColors[500],
        openBackgroundColor: 'background.menu',
      },
    },
    headerModuleMenuPaper: {
      border: `1px solid ${neutral[200]}`,
      borderRadius: '10px',
      padding: '12px 16px',
      boxShadow: '0px 4px 12px rgba(30, 42, 49, 0.12)',
      minWidth: 220,
      marginTop: 0.5,
      menuListPaddingY: 1,
      menuListPaddingX: 0,
      light: {
        backgroundColor: '#ffffff',
        backgroundColorAlt: '#ffffff',
      },
      dark: {
        backgroundColor: 'background.menu',
        backgroundColorAlt: 'background.menu',
      },
    },
    contextMenuPaper: {
      borderRadius: '10px 0px 10px 10px',
      padding: '16px 48px 16px 0px',
      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.06)',
      width: {xs: '250px', sm: '300px'},
      light: {
        backgroundColor: orangeColors[5],
        backgroundColorAlt: orangeColors[5],
      },
      dark: {
        backgroundColor: 'background.menu',
        backgroundColorAlt: 'background.menu',
      },
    },
    avatarMenuPaper: {
      border: `1px solid ${neutral[200]}`,
      borderRadius: '10px',
      padding: '12px 16px',
      boxShadow: '0px 4px 12px rgba(30, 42, 49, 0.12)',
      minWidth: 220,
      light: {
        backgroundColor: '#ffffff',
        backgroundColorAlt: '#ffffff',
      },
      dark: {
        backgroundColor: 'background.menu',
        backgroundColorAlt: 'background.menu',
      },
    },
    menuItem: {
      backgroundColor: 'transparent',
      hoverBackgroundColor: neutral[200],
      color: greenColors[700],
      borderRadius: '8px',
      minHeight: 40,
      marginX: 0,
      marginY: 0.25,
      paddingX: 1,
      paddingY: 1,
      fontWeight: 500,
      iconMinWidth: 32,
    },
  };
};

export const getHeaderNavTokens = (
  version: DesignSystemVersion = ACTIVE_DESIGN_SYSTEM_VERSION,
): HeaderNavDefinition => {
  if (version === '20260616') {
    return buildHeaderNav20260616();
  }

  return buildHeaderNav20260615(version);
};

export const headerNav = getHeaderNavTokens();
